import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import axios from 'axios';
import { API_BASE } from '../../lib/utils';
import { toast } from 'sonner';
import { Calendar, Clock, Video, User, Check, ChevronLeft, ChevronRight, Zap } from 'lucide-react';

const Booking = () => {
  const { user } = useAuth();
  const [slots, setSlots] = useState([]);
  const [counsellors, setCounsellors] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [selectedCounsellor, setSelectedCounsellor] = useState('');
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [bookingData, setBookingData] = useState(null);
  const [quickRunning, setQuickRunning] = useState(false);
  const [weekOffset, setWeekOffset] = useState(0);

  const fetchData = useCallback(async () => {
    try {
      const [slotsRes, dataRes, dbCounsellorsRes] = await Promise.all([
        axios.get(`${API_BASE}/booking/slots`),
        axios.get(`${API_BASE}/booking/data/${user.id}`),
        axios.get(`${API_BASE}/counsellor/list`).catch(() => ({ data: { counsellors: [] } })),
      ]);
      setSlots(slotsRes.data.slots || []);
      // Prefer registered counsellors from DB, fall back to static list
      const dbCounsellors = (dbCounsellorsRes.data.counsellors || []).map(c => ({
        counsellor: c.name, speciality: c.speciality || 'Career Guidance', id: c.id,
      }));
      const allCounsellors = dbCounsellors.length > 0 ? dbCounsellors : (slotsRes.data.counsellors || []);
      setCounsellors(allCounsellors);
      if (allCounsellors.length > 0) {
        setSelectedCounsellor(allCounsellors[0].counsellor);
      }
      if (dataRes.data.completed) {
        setCompleted(true);
        setBookingData(dataRes.data);
      }
    } catch (err) {
      console.error('Failed to load booking data:', err);
    } finally {
      setLoading(false);
    }
  }, [user.id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleBook = async () => {
    if (!selectedSlot || !selectedCounsellor) {
      toast.error('Please select a time slot and counsellor');
      return;
    }
    setBooking(true);
    try {
      await axios.post(`${API_BASE}/booking/save/${user.id}`, {
        slot_id: selectedSlot.id,
        date: selectedSlot.date,
        time: selectedSlot.time,
        end_time: selectedSlot.end_time,
        counsellor: selectedCounsellor
      });
      toast.success('Session booked successfully!');

      // Send candidate report to counsellor's inbox
      const counsellorDoc = counsellors.find(c => c.counsellor === selectedCounsellor || c.name === selectedCounsellor);
      if (counsellorDoc?.id) {
        try {
          await axios.post(`${API_BASE}/candidate-report/send-to-counsellor`, {
            user_id: user.id,
            counsellor_id: counsellorDoc.id,
          });
          toast.success('Your candidate report has been sent to the counsellor.');
        } catch (e) { console.error('Failed to send report:', e); }
      }

      setCompleted(true);
      setBookingData({
        date: selectedSlot.date,
        time: selectedSlot.time,
        end_time: selectedSlot.end_time,
        counsellor: selectedCounsellor,
        duration: '1.5 hours',
        type: 'zoom'
      });
    } catch (err) {
      toast.error('Failed to book session');
    } finally {
      setBooking(false);
    }
  };

  const handleQuickComplete = async () => {
    setQuickRunning(true);
    try {
      await axios.post(`${API_BASE}/demo/quick-complete-booking/${user.id}`);
      toast.success('Booking auto-completed!');
      setCompleted(true);
      await fetchData();
    } catch (err) {
      toast.error('Quick complete failed');
    } finally {
      setQuickRunning(false);
    }
  };

  if (loading) {
    return <div className="p-12 flex items-center justify-center"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#0071e3]" /></div>;
  }

  // Group slots by date
  const groupedSlots = {};
  slots.forEach(s => {
    if (!groupedSlots[s.date]) groupedSlots[s.date] = { label: s.day_label, slots: [] };
    groupedSlots[s.date].slots.push(s);
  });
  const dateKeys = Object.keys(groupedSlots);
  const daysPerPage = 5;
  const visibleDates = dateKeys.slice(weekOffset * daysPerPage, (weekOffset + 1) * daysPerPage);
  const canGoPrev = weekOffset > 0;
  const canGoNext = (weekOffset + 1) * daysPerPage < dateKeys.length;

  if (completed && bookingData) {
    const formatDate = (dateStr) => {
      if (!dateStr) return '';
      const d = new Date(dateStr + 'T00:00:00');
      return d.toLocaleDateString('en-IE', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
    };

    return (
      <div className="p-4 sm:p-8 md:p-12">
        <div className="max-w-3xl mx-auto text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-100 flex items-center justify-center">
            <Check className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-4xl sm:text-5xl font-semibold text-[#1d1d1f] mb-3" data-testid="booking-complete-title">
            Session Booked
          </h1>
          <p className="text-lg text-[#86868b] mb-8">Your career counselling session has been confirmed.</p>

          <div className="bg-white rounded-2xl border border-[#d2d2d7] p-6 sm:p-8 text-left space-y-4 mb-6">
            <div className="flex items-center gap-3">
              <Calendar className="w-5 h-5 text-[#0071e3]" />
              <div>
                <span className="text-sm text-[#86868b]">Date</span>
                <p className="text-base font-semibold text-[#1d1d1f]">{formatDate(bookingData.date)}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Clock className="w-5 h-5 text-[#0071e3]" />
              <div>
                <span className="text-sm text-[#86868b]">Time</span>
                <p className="text-base font-semibold text-[#1d1d1f]">{bookingData.time} - {bookingData.end_time} ({bookingData.duration})</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <User className="w-5 h-5 text-[#0071e3]" />
              <div>
                <span className="text-sm text-[#86868b]">Counsellor</span>
                <p className="text-base font-semibold text-[#1d1d1f]">{bookingData.counsellor}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Video className="w-5 h-5 text-[#0071e3]" />
              <div>
                <span className="text-sm text-[#86868b]">Format</span>
                <p className="text-base font-semibold text-[#1d1d1f]">Zoom Video Call</p>
              </div>
            </div>
          </div>

          <p className="text-sm text-[#86868b] mb-4">
            A Zoom link will be sent to your email 24 hours before the session.
          </p>
          <Button
            onClick={() => { setCompleted(false); setSelectedSlot(null); }}
            variant="outline"
            className="rounded-full border-[#d2d2d7] text-[#1d1d1f]"
            data-testid="reschedule-btn"
          >
            Reschedule Session
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-8 md:p-12">
      <div className="max-w-4xl mx-auto">
        {/* Quick Complete Banner */}
        <div className="bg-[#FFF8E1] border border-[#FFE082] rounded-2xl p-4 mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3" data-testid="booking-demo-banner">
          <div className="text-sm text-[#1d1d1f]">
            <span className="font-semibold">Investor Demo:</span> Skip to see a confirmed booking instantly
          </div>
          <Button
            onClick={handleQuickComplete}
            disabled={quickRunning}
            className="bg-[#FF9500] hover:bg-[#E68600] text-white rounded-full px-5 text-sm"
            data-testid="quick-complete-booking-btn"
          >
            <Zap className="w-3.5 h-3.5 mr-1.5" />
            {quickRunning ? 'Completing...' : 'Quick Complete'}
          </Button>
        </div>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl sm:text-5xl font-semibold text-[#1d1d1f] mb-3" data-testid="booking-title">
            Career Counsellor Booking
          </h1>
          <p className="text-lg text-[#86868b]">
            Book a 1.5 hour mock Zoom session with a career counsellor to review your results and next steps.
          </p>
        </div>

        {/* Counsellor Selection */}
        <div className="bg-white rounded-2xl border border-[#d2d2d7] p-6 mb-6">
          <h2 className="text-xl font-semibold text-[#1d1d1f] mb-4 flex items-center gap-2">
            <User className="w-5 h-5 text-[#0071e3]" />
            Select Counsellor
          </h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {counsellors.map((c) => (
              <button
                key={c.counsellor}
                onClick={() => setSelectedCounsellor(c.counsellor)}
                className={`p-4 rounded-xl border-2 text-left transition-all ${
                  selectedCounsellor === c.counsellor
                    ? 'border-[#0071e3] bg-blue-50'
                    : 'border-[#d2d2d7] hover:border-[#86868b]'
                }`}
                data-testid={`counsellor-${c.counsellor.replace(/\s+/g, '-').toLowerCase()}`}
              >
                <p className="font-semibold text-[#1d1d1f] text-sm">{c.counsellor}</p>
                <p className="text-xs text-[#86868b] mt-1">{c.speciality}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Calendar View */}
        <div className="bg-white rounded-2xl border border-[#d2d2d7] p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-[#1d1d1f] flex items-center gap-2">
              <Calendar className="w-5 h-5 text-[#34C759]" />
              Available Time Slots
            </h2>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setWeekOffset(w => w - 1)}
                disabled={!canGoPrev}
                className="w-8 h-8 flex items-center justify-center rounded-lg border border-[#d2d2d7] text-[#86868b] hover:bg-[#f5f5f7] disabled:opacity-30 transition-colors"
                data-testid="prev-week-btn"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => setWeekOffset(w => w + 1)}
                disabled={!canGoNext}
                className="w-8 h-8 flex items-center justify-center rounded-lg border border-[#d2d2d7] text-[#86868b] hover:bg-[#f5f5f7] disabled:opacity-30 transition-colors"
                data-testid="next-week-btn"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 md:grid-cols-5 gap-4">
            {visibleDates.map(dateKey => {
              const day = groupedSlots[dateKey];
              const dayName = day.label.split(',')[0];
              const dateShort = day.label.split(', ')[1];
              return (
                <div key={dateKey} className="border border-[#e5e5ea] rounded-xl overflow-hidden">
                  <div className="bg-[#f5f5f7] px-3 py-2 text-center">
                    <p className="text-xs font-semibold text-[#1d1d1f]">{dayName}</p>
                    <p className="text-xs text-[#86868b]">{dateShort}</p>
                  </div>
                  <div className="p-2 space-y-1.5">
                    {day.slots.map(slot => {
                      const isSelected = selectedSlot?.id === slot.id;
                      return (
                        <button
                          key={slot.id}
                          onClick={() => setSelectedSlot(slot)}
                          className={`w-full px-2 py-2 rounded-lg text-xs font-medium transition-all ${
                            isSelected
                              ? 'bg-[#0071e3] text-white'
                              : 'bg-white border border-[#d2d2d7] text-[#1d1d1f] hover:border-[#0071e3] hover:bg-blue-50'
                          }`}
                          data-testid={`slot-${slot.id}`}
                        >
                          {slot.time} - {slot.end_time}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          {selectedSlot && (
            <div className="mt-4 p-4 bg-blue-50 rounded-xl border border-blue-200">
              <p className="text-sm text-[#1d1d1f]">
                <span className="font-semibold">Selected:</span> {selectedSlot.day_label} at {selectedSlot.time} - {selectedSlot.end_time} ({selectedSlot.duration})
              </p>
            </div>
          )}
        </div>

        {/* Book Button */}
        <Button
          onClick={handleBook}
          disabled={booking || !selectedSlot || !selectedCounsellor}
          className="w-full bg-[#0071e3] hover:bg-[#0077ed] text-white rounded-full h-12 text-base"
          data-testid="confirm-booking-btn"
        >
          <Video className="w-4 h-4 mr-2" />
          {booking ? 'Booking...' : 'Confirm Zoom Session Booking'}
        </Button>
      </div>
    </div>
  );
};

export default Booking;
